import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { closeLoading, showLoading } from '../../Reducer/LoadingReducer';
import { handleQuerySuccess } from '../useQuriesSuccess';
import useErrorHandler from '../useErrorHandler';
import { useRef } from 'react';
/**
 * 공통 검색용 Mutation 훅
 *
 * 목적
 * - "조회지만 조건에 따라 실행"해야 하는 API를 useMutation으로 통일
 * - 일관된 로딩 표시(showLoading / closeLoading)
 * - 성공 시 공통 응답 처리(handleQuerySuccess)
 *
 * 특징
 * - 조회(useQuery)와 달리 자동 실행되지 않음
 * - run(params)를 호출해야 실제 API 요청 발생
 */
const useSearchMutaion = (
  mutationFn,
  successCallback = null,
  showLoadingFlag = true
) => {
  const dispatch = useDispatch();
  const { handleError } = useErrorHandler();

  const loadingTimer = useRef(null);

  const mutation = useMutation({
    mutationFn,

    onMutate: () => {
      if (showLoadingFlag) {
        if (loadingTimer.current) clearTimeout(loadingTimer.current);

        loadingTimer.current = setTimeout(() => {
          dispatch(showLoading());
        }, 500);
      }
    },

    onError: async (error, variables, context) => {
      if (error.shouldShowDialog) {
        return;
      }

      await handleError(error, {
        url: error.config?.url,
        method: error.config?.method,
      });
    },

    onSettled: () => {
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
        loadingTimer.current = null;
      }

      if (showLoadingFlag) {
        dispatch(closeLoading());
      }
    },
  });

  const run = (params, successOptions = null) => {
    mutation.mutate(params, {
      onSuccess: (response) => {
        handleQuerySuccess(response, successOptions ?? successCallback);
      },
    });
  };

  const runAsync = async (params, successOptions = null) => {
    try {
      const response = await mutation.mutateAsync(params);
      return handleQuerySuccess(response, successOptions ?? successCallback);
    } catch (error) {
      // async 호출 시 에러 처리는 내부 onError에서 수행됨
      throw error;
    }
  };

  return { ...mutation, run, runAsync };
};

export default useSearchMutaion;
